import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ArrowLeft, Scale, Loader2, Edit, FileText, Calendar,
    CheckCircle2, Clock, Paperclip, Tag, PlayCircle, Plus,
    XCircle, CheckSquare, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { TasksList } from '../components/TasksList';
import { CreateTaskModal } from '../components/CreateTaskModal';
import { LinkTrademarksModal } from '../components/LinkTrademarksModal';
import { ClientDocuments } from '../components/ClientDocuments';
import { ProcedureTimeline } from '../components/ProcedureTimeline';
import { CreateEventModal } from '../components/CreateEventModal';
import { UploadDocumentModal } from '../components/UploadDocumentModal';
import { toast } from 'sonner';
import { getProcedureTemplate, PROCEDURE_ICONS, PROCEDURE_COLORS, PROCEDURE_LABELS } from '../constants/procedureTemplates';
import { proceduresApi } from '../api/procedures';
import EditProcedureModal from '../components/EditProcedureModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROCEDURE_STATUS_LABELS = {
    DRAFT: 'Borrador',
    OPEN: 'Abierto',
    ACTIVE: 'Activo',
    WAITING_CLIENT: 'Esperando al Cliente',
    WAITING_AUTHORITY: 'Esperando Autoridad',
    REQUIRED_ACTION: 'Acción Requerida',
    RESOLVED: 'Resuelto',
    CLOSED: 'Cerrado',
    ARCHIVED: 'Archivado',
    SUSPENDED: 'Suspendido',
    // Mapping for lowercase backend values if they leak
    draft: 'Borrador',
    open: 'Abierto',
    active: 'Activo',
    waiting_client: 'Esperando al Cliente',
    waiting_authority: 'Esperando Autoridad',
    required_action: 'Acción Requerida',
    resolved: 'Resuelto',
    closed: 'Cerrado',
    archived: 'Archivado',
    suspended: 'Suspendido'
};

const PROCEDURE_STATUS_COLORS = {
    DRAFT: 'bg-gray-100 text-gray-700',
    OPEN: 'bg-blue-100 text-blue-700',
    ACTIVE: 'bg-blue-100 text-blue-700',
    WAITING_CLIENT: 'bg-yellow-100 text-yellow-700',
    WAITING_AUTHORITY: 'bg-orange-100 text-orange-700',
    REQUIRED_ACTION: 'bg-red-100 text-red-700',
    RESOLVED: 'bg-green-100 text-green-700',
    CLOSED: 'bg-gray-800 text-white',
    ARCHIVED: 'bg-gray-100 text-gray-500',
    SUSPENDED: 'bg-yellow-100 text-yellow-700',
    // Mapping for lowercase
    draft: 'bg-gray-100 text-gray-700',
    open: 'bg-blue-100 text-blue-700',
    active: 'bg-blue-100 text-blue-700',
    waiting_client: 'bg-yellow-100 text-yellow-700',
    waiting_authority: 'bg-orange-100 text-orange-700',
    required_action: 'bg-red-100 text-red-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-800 text-white',
    archived: 'bg-gray-100 text-gray-500',
    suspended: 'bg-yellow-100 text-yellow-700'
};

const PROCEDURE_PRIORITY_COLORS = {
    LOW: 'bg-gray-100 text-gray-600',
    MEDIUM: 'bg-blue-100 text-blue-600',
    HIGH: 'bg-orange-100 text-orange-600',
    URGENT: 'bg-red-100 text-red-600',
    // Mapping for lowercase
    low: 'bg-gray-100 text-gray-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    urgent: 'bg-red-100 text-red-600'
};

const PROCEDURE_PRIORITY_LABELS = {
    LOW: 'Baja',
    MEDIUM: 'Media',
    HIGH: 'Alta',
    URGENT: 'Urgente',
    // Mapping for lowercase
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente'
};

export const ProcedureDetail = () => {
    const { id } = useParams();
    const { token, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [procedure, setProcedure] = useState(null);
    const [client, setClient] = useState(null);
    const [trademarks, setTrademarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
    const [linkTrademarksModalOpen, setLinkTrademarksModalOpen] = useState(false);
    const [prefilledTask, setPrefilledTask] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteConfirmCheckbox, setDeleteConfirmCheckbox] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // To force refresh of documents or timeline when needed
    const [refreshKey, setRefreshKey] = useState(0);
    const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
    const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    useEffect(() => {
        if (id) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refreshKey]);

    async function fetchData() {
        try {
            const procedureData = await proceduresApi.getProcedure(token, id);

            setProcedure(procedureData);

            // Fetch related data
            const promises = [];

            // Fetch client
            if (procedureData.client_id) {
                promises.push(
                    axios.get(`${API}/clients/${procedureData.client_id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(res => setClient(res.data))
                );
            }

            // Fetch linked trademarks
            if (procedureData.trademark_links && procedureData.trademark_links.length > 0) {
                const trademarkIds = procedureData.trademark_links.map(link => link.trademark_id);
                promises.push(
                    axios.get(`${API}/trademarks`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(res => {
                        const linkedTms = res.data.filter(tm =>
                            trademarkIds.includes(tm.id)
                        );
                        setTrademarks(linkedTms);
                    })
                );
            } else if (procedureData.trademark_ids && procedureData.trademark_ids.length > 0) {
                // Fallback to old format
                promises.push(
                    axios.get(`${API}/trademarks`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(res => {
                        const linkedTms = res.data.filter(tm =>
                            procedureData.trademark_ids.includes(tm.id)
                        );
                        setTrademarks(linkedTms);
                    })
                );
            }

            await Promise.all(promises);
        } catch (error) {
            console.error('Error fetching procedure:', error);
            toast.error('Error al cargar procedimiento');
        } finally {
            setLoading(false);
        }
    }

    const handleCreateSuggestedTask = (suggestion) => {
        setPrefilledTask({
            title: suggestion.title,
            priority: suggestion.priority,
            description: suggestion.description
        });
        setCreateTaskModalOpen(true);
    };

    const handleCloseProcedure = () => {
        // TODO: Implement close procedure modal
        toast.info("Funcionalidad de cierre en desarrollo");
    };

    const handleDeleteProcedure = async () => {
        if (deleteConfirmText !== 'ELIMINAR' || !deleteConfirmCheckbox) {
            toast.error('Debe confirmar escribiendo ELIMINAR y marcando la casilla');
            return;
        }

        setDeleteLoading(true);
        try {
            await axios.delete(`${API}/procedures/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Procedimiento eliminado exitosamente');
            navigate('/procedures');
        } catch (error) {
            console.error('Error deleting procedure:', error);
            toast.error('Error al eliminar el procedimiento', {
                description: error.response?.data?.detail || 'Intente nuevamente'
            });
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setDeleteConfirmText('');
            setDeleteConfirmCheckbox(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleUpdateProcedure = async (updatedData) => {
        try {
            const response = await axios.put(`${API}/procedures/${id}`, updatedData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProcedure(response.data);
            setIsEditModalOpen(false);
            toast.success("Procedimiento actualizado");
        } catch (error) {
            console.error('Error updating procedure:', error);
            toast.error('Error al actualizar procedimiento');
        }
    };

    if (loading) {
        return (
            <>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
            </>
        );
    }

    if (!procedure) {
        return (
            <>
                <div className="text-center py-12">
                    <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Procedimiento no encontrado</p>
                    <Button variant="link" onClick={() => navigate(-1)}>
                        Volver
                    </Button>
                </div>
            </>
        );
    }

    const template = getProcedureTemplate(procedure.procedure_type);
    const TypeIcon = PROCEDURE_ICONS[procedure.procedure_type] || Scale;

    return (
        <>
            <div className="space-y-6">
                {/* Header Navigation */}
                <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Volver
                    </Button>
                </div>

                {/* Procedure Header Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-6">
                        <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${PROCEDURE_COLORS[procedure.procedure_type]?.split(' ')[1] || 'bg-gray-100'
                                }`}>
                                <TypeIcon className={`h-6 w-6 md:h-8 md:h-8 ${PROCEDURE_COLORS[procedure.procedure_type]?.split(' ')[0] || 'text-gray-600'
                                    }`} />
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 truncate">
                                    {procedure.title}
                                </h1>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className={`${PROCEDURE_STATUS_COLORS[procedure.status] || 'bg-gray-100 text-gray-800'} border-0 text-[10px] h-5 shadow-none`}>
                                        {PROCEDURE_STATUS_LABELS[procedure.status] || procedure.status}
                                    </Badge>
                                    <Badge variant="outline" className={`${PROCEDURE_PRIORITY_COLORS[procedure.priority] || 'bg-gray-100 text-gray-800'} border-0 text-[10px] h-5 shadow-none`}>
                                        {PROCEDURE_PRIORITY_LABELS[procedure.priority] || procedure.priority}
                                    </Badge>
                                    <Badge variant="outline" className={`${PROCEDURE_COLORS[procedure.procedure_type]} text-[10px] h-5 shadow-none`}>
                                        {template.label}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {/* CTAs */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(true)} className="text-xs h-9">
                                <Edit className="h-3.5 w-3.5 mr-2" />
                                <span className="hidden sm:inline">Editar</span>
                                <span className="sm:hidden">Edit</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setCreateEventModalOpen(true)} className="text-xs h-9">
                                <Plus className="h-3.5 w-3.5 mr-2" />
                                <span className="truncate">Actuación</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setPrefilledTask(null); setCreateTaskModalOpen(true); }} className="text-xs h-9">
                                <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                                <span className="truncate">Acción</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setUploadDocumentModalOpen(true)} className="text-xs h-9">
                                <Paperclip className="h-3.5 w-3.5 mr-2" />
                                <span className="truncate">Doc</span>
                            </Button>
                            {procedure.status !== 'CLOSED' && (
                                <Button variant="destructive" size="sm" onClick={handleCloseProcedure} className="text-xs h-9 col-span-2 md:col-span-1 lg:w-auto">
                                    <XCircle className="h-3.5 w-3.5 mr-2" />
                                    Cerrar
                                </Button>
                            )}
                            {isAdmin && isAdmin() && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setDeleteConfirmText(''); setDeleteConfirmCheckbox(false); setDeleteDialogOpen(true); }}
                                    className="text-xs h-9 text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto border border-transparent hover:border-red-100 hidden lg:flex"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                                    Eliminar
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Quick Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-100">
                        <div className="bg-gray-50/50 p-2.5 rounded-lg border border-gray-100/50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Expendiente</div>
                            <div className="text-xs font-bold text-gray-900 truncate">
                                {procedure.procedure_number || 'S/N'}
                            </div>
                        </div>
                        <div className="bg-gray-50/50 p-2.5 rounded-lg border border-gray-100/50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Autoridad</div>
                            <div className="text-xs font-bold text-gray-900 truncate">
                                {procedure.authority || '-'}
                            </div>
                        </div>
                        <div className="bg-gray-50/50 p-2.5 rounded-lg border border-gray-100/50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Cliente</div>
                            <div className="text-xs font-bold text-blue-600 truncate cursor-pointer hover:underline" onClick={() => navigate(`/clients/${procedure.client_id}`)}>
                                {client ? client.nombre : '...'}
                            </div>
                        </div>
                        <div className="bg-gray-50/50 p-2.5 rounded-lg border border-gray-100/50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Responsable</div>
                            <div className="text-xs font-bold text-gray-900 truncate">
                                {procedure.responsible_user_id ? `Usuario ${procedure.responsible_user_id.slice(0, 6)}` : 'S/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs defaultValue="summary" className="space-y-6">
                    <TabsList className="bg-gray-100/80 p-1 w-full flex justify-start overflow-x-auto no-scrollbar scroll-smooth">
                        <TabsTrigger
                            value="summary"
                            className="flex-shrink-0 text-xs md:text-sm px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                        >
                            <FileText className="h-4 w-4 mr-2" />
                            Resumen
                        </TabsTrigger>
                        <TabsTrigger
                            value="timeline"
                            className="flex-shrink-0 text-xs md:text-sm px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                        >
                            <Calendar className="h-4 w-4 mr-2" />
                            Línea de Tiempo
                        </TabsTrigger>
                        <TabsTrigger
                            value="tasks"
                            className="flex-shrink-0 text-xs md:text-sm px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                        >
                            <CheckSquare className="h-4 w-4 mr-2" />
                            Acciones
                        </TabsTrigger>
                        <TabsTrigger
                            id="documents-tab-trigger"
                            value="documents"
                            className="flex-shrink-0 text-xs md:text-sm px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                        >
                            <Paperclip className="h-4 w-4 mr-2" />
                            Documentos
                        </TabsTrigger>
                        <TabsTrigger
                            value="trademarks"
                            className="flex-shrink-0 text-xs md:text-sm px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                        >
                            <Tag className="h-4 w-4 mr-2" />
                            Marcas ({trademarks.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Summary Tab */}
                    <TabsContent value="summary" className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Main Info */}
                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white rounded-lg border border-gray-200 p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Información General</h3>

                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-sm font-medium text-gray-500 mb-1">Descripción</div>
                                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                {procedure.description || 'Sin descripción'}
                                            </div>
                                        </div>

                                        {/* Dynamic Fields Section */}
                                        {template.fields && template.fields.length > 0 && (
                                            <div className="pt-4 border-t border-gray-200">
                                                <h4 className="text-sm font-medium text-gray-700 mb-3 block">
                                                    Detalles Específicos ({template.label})
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {template.fields.map(field => (
                                                        <div key={field.key} className="bg-gray-50 p-3 rounded-md border border-gray-100">
                                                            <div className="text-xs text-gray-500 mb-1">{field.label}</div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {procedure.metadata?.[field.key] || <span className="text-gray-400 italic">No especificado</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                                            <div>
                                                <div className="text-sm font-medium text-gray-500 mb-1">Fecha de Presentación</div>
                                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                                    <Calendar className="h-4 w-4 text-gray-400" />
                                                    {formatDate(procedure.filing_date)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-500 mb-1">Próximo Plazo</div>
                                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-gray-400" />
                                                    {formatDate(procedure.deadline_date)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-500 mb-1">Fecha de Resolución</div>
                                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                                    <CheckCircle2 className="h-4 w-4 text-gray-400" />
                                                    {formatDate(procedure.resolution_date)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Suggestions Panel */}
                            <div className="space-y-6">
                                {template.suggestedTasks && template.suggestedTasks.length > 0 && (
                                    <div className="bg-blue-50 rounded-lg border border-blue-100 p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <PlayCircle className="h-5 w-5 text-blue-600" />
                                            <h3 className="text-base font-semibold text-blue-900">Sugerencias</h3>
                                        </div>
                                        <div className="space-y-3">
                                            {template.suggestedTasks.map((task, idx) => (
                                                <div key={idx} className="bg-white p-3 rounded-md border border-blue-100 shadow-sm">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <span className="font-medium text-sm text-gray-900 leading-tight">
                                                            {task.title}
                                                        </span>
                                                        <Badge className={`${PROCEDURE_PRIORITY_COLORS[task.priority]} text-[10px] px-1.5 h-5`}>
                                                            {PROCEDURE_PRIORITY_LABELS[task.priority]}
                                                        </Badge>
                                                    </div>
                                                    {task.description && (
                                                        <div className="text-xs text-gray-500 mb-3 leading-relaxed">
                                                            {task.description}
                                                        </div>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="w-full text-blue-700 hover:text-blue-800 hover:bg-blue-50 text-xs h-8 border border-blue-100"
                                                        onClick={() => handleCreateSuggestedTask(task)}
                                                    >
                                                        <Plus className="h-3 w-3 mr-1.5" />
                                                        Agregar Tarea
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Timeline Tab */}
                    <TabsContent value="timeline">
                        <ProcedureTimeline
                            key={refreshKey}
                            procedureId={id}
                            token={token}
                            onAddEvent={() => setCreateEventModalOpen(true)}
                        />
                    </TabsContent>

                    {/* Tasks Tab */}
                    <TabsContent value="tasks">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Acciones y Tareas</h3>
                                <Button size="sm" onClick={() => { setPrefilledTask(null); setCreateTaskModalOpen(true); }}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Nueva Tarea
                                </Button>
                            </div>
                            <TasksList
                                procedureId={id}
                                token={token}
                                onRefresh={fetchData}
                            />
                        </div>
                    </TabsContent>

                    {/* Documents Tab */}
                    <TabsContent value="documents">
                        <ClientDocuments
                            key={refreshKey}
                            entityType="procedure"
                            entityId={id}
                            token={token}
                            folderId={null} // Procedures don't filter internal docs by folder usually, or we can look it up
                        />
                    </TabsContent>

                    {/* Linked Trademarks Tab */}
                    <TabsContent value="trademarks">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Marcas Vinculadas</h3>
                                <Button size="sm" onClick={() => setLinkTrademarksModalOpen(true)}>
                                    <Tag className="h-4 w-4 mr-2" />
                                    Gestionar Marcas
                                </Button>
                            </div>

                            {procedure.trademark_links && procedure.trademark_links.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                    <p>No hay marcas vinculadas</p>
                                    <p className="text-sm mt-2">Vincula marcas relacionadas con este procedimiento</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {procedure.trademark_links.map(link => {
                                        const tm = trademarks.find(t => t.id === link.trademark_id);
                                        if (!tm) return null;

                                        const roleColors = {
                                            actor: 'bg-blue-100 text-blue-700 border-blue-200',
                                            defendant: 'bg-red-100 text-red-700 border-red-200',
                                            reference: 'bg-gray-100 text-gray-700 border-gray-200',
                                            other: 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                        };

                                        const roleLabels = {
                                            actor: 'Actor',
                                            defendant: 'Demandado',
                                            reference: 'Referencia',
                                            other: 'Otro'
                                        };

                                        return (
                                            <div
                                                key={link.trademark_id}
                                                onClick={() => navigate(`/trademarks/${tm.id}`)}
                                                className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                {tm.imagen_url ? (
                                                    <img
                                                        src={tm.imagen_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${tm.imagen_url}` : tm.imagen_url}
                                                        alt={tm.denominacion}
                                                        className="w-12 h-12 object-contain rounded border border-gray-200 bg-gray-50"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-lg text-gray-500 font-medium">
                                                        {(tm.denominacion || 'M').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {tm.denominacion || 'Sin denominación'}
                                                        </div>
                                                        <Badge className={`${roleColors[link.role] || roleColors.other} border text-xs`}>
                                                            {roleLabels[link.role] || link.role}
                                                        </Badge>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Expediente: {tm.expediente} • Registro: {tm.registro || 'Pendiente'}
                                                    </div>
                                                    {link.notes && (
                                                        <div className="text-xs text-gray-600 mt-1 italic">
                                                            {link.notes}
                                                        </div>
                                                    )}
                                                </div>
                                                <ArrowLeft className="h-4 w-4 text-gray-400 rotate-180" />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Create Task Modal */}
            <CreateTaskModal
                procedureId={id}
                token={token}
                open={createTaskModalOpen}
                onClose={() => setCreateTaskModalOpen(false)}
                onTaskCreated={() => { fetchData(); toast.success("Tarea creada"); }}
                prefilledData={prefilledTask}
            />

            {/* Link Trademarks Modal */}
            <LinkTrademarksModal
                procedureId={id}
                clientId={procedure?.client_id}
                token={token}
                open={linkTrademarksModalOpen}
                onClose={() => setLinkTrademarksModalOpen(false)}
                onLinksUpdated={fetchData}
                currentLinks={procedure?.trademark_links || []}
            />

            {/* Event Modal */}
            <CreateEventModal
                procedureId={id}
                token={token}
                open={createEventModalOpen}
                onClose={() => setCreateEventModalOpen(false)}
                onEventCreated={() => setRefreshKey(prev => prev + 1)}
            />

            {/* Upload Document Modal */}
            <UploadDocumentModal
                open={uploadDocumentModalOpen}
                onClose={() => setUploadDocumentModalOpen(false)}
                token={token}
                entityId={id}
                entityType="procedure"
                onUploadComplete={() => setRefreshKey(prev => prev + 1)}
            />

            <EditProcedureModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                procedure={procedure}
                onUpdate={handleUpdateProcedure}
                token={token}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">⚠️ Eliminar procedimiento permanentemente</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-4 text-gray-700">
                            <p>
                                Estás a punto de eliminar el procedimiento <strong>{procedure?.title}</strong>.
                                Esta acción <strong className="text-red-600 font-black">NO ES REVERSIBLE</strong> y eliminará todos los datos asociados.
                            </p>

                            <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-start gap-3">
                                <Trash2 className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-red-800">
                                    <strong>¿Pérdida de datos?</strong>
                                    <p className="mt-1">
                                        Sí. Se perderá el historial, notas, tareas documentadas y todas las referencias. Las marcas continuarán intactas en el sistema, pero ya no estarán asociadas a este procedimiento.
                                    </p>
                                </div>
                            </div>

                            <p className="text-sm font-medium">
                                Para confirmar tu decisión, por favor:
                            </p>

                            <ol className="list-decimal pl-5 text-sm space-y-3 font-medium text-gray-900">
                                <li>
                                    Escribe la palabra <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded select-all font-black">ELIMINAR</span> aquí abajo:
                                    <Input
                                        type="text"
                                        placeholder="Escribe ELIMINAR"
                                        className="mt-2 font-mono uppercase bg-white border-gray-300 h-10 w-full focus-visible:ring-red-500"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                                    />
                                </li>
                                <li className="pt-2">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <Checkbox
                                            id="delete-terms"
                                            className="mt-1 border-gray-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                                            checked={deleteConfirmCheckbox}
                                            onCheckedChange={setDeleteConfirmCheckbox}
                                        />
                                        <span className="leading-snug">
                                            Entiendo que esta acción es irrevocable y quiero borrar este procedimiento definitivamente del sistema.
                                        </span>
                                    </label>
                                </li>
                            </ol>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 border-t pt-4">
                        <AlertDialogCancel disabled={deleteLoading} onClick={() => { setDeleteConfirmText(''); setDeleteConfirmCheckbox(false); }}>
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteProcedure();
                            }}
                            disabled={deleteConfirmText !== 'ELIMINAR' || !deleteConfirmCheckbox || deleteLoading}
                            className={`bg-red-600 hover:bg-red-700 text-white font-bold transition-all ${(deleteConfirmText === 'ELIMINAR' && deleteConfirmCheckbox)
                                ? 'shadow-lg shadow-red-200 ring-2 ring-red-600 ring-offset-2'
                                : 'opacity-50'
                                }`}
                        >
                            {deleteLoading ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Borrando...</>
                            ) : (
                                <><Trash2 className="mr-2 h-4 w-4" /> Eliminar Definitivamente</>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
